import React from 'react';
import { Grid, Paper, Box, Typography, Chip, Tooltip } from '@material-ui/core';
import {
  AttachMoney,
  AccountBalanceWallet,
  Receipt,
  TrendingUp,
  ShoppingCart,
  LocalOffer,
  ArrowUpward,
  ArrowDownward,
  InfoOutlined,
} from '@material-ui/icons';
import { formattedCurrency } from '../../../utils/utils';

const DeltaBadge = ({ deltaPct, invertColors = false }) => {
  const num = Number(deltaPct || 0);
  if (num === 0) {
    return (
      <Chip
        size="small"
        label="0%"
        style={{
          backgroundColor: '#f1f5f9',
          color: '#64748b',
          fontWeight: 600,
          height: 22,
          fontSize: '0.72rem',
        }}
      />
    );
  }

  const isPositive = num > 0;
  const isGood = invertColors ? !isPositive : isPositive;
  const bg = isGood ? '#dcfce7' : '#fee2e2';
  const color = isGood ? '#15803d' : '#b91c1c';

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      px={1}
      py={0.25}
      style={{
        backgroundColor: bg,
        color,
        borderRadius: 12,
        fontSize: '0.74rem',
        fontWeight: 700,
      }}
    >
      {isPositive ? (
        <ArrowUpward style={{ fontSize: 13, marginRight: 2 }} />
      ) : (
        <ArrowDownward style={{ fontSize: 13, marginRight: 2 }} />
      )}
      {Math.abs(num).toFixed(1)}%
    </Box>
  );
};

const OverviewKpiGrid = ({ kpis }) => {
  if (!kpis) return null;

  const cards = [
    {
      key: 'grossSales',
      title: 'Vendas Realizadas',
      tooltip: 'Valor total bruto das vendas registradas no período selecionado.',
      value: formattedCurrency(kpis.grossSales?.value || 0),
      previousLabel: `Anterior: ${formattedCurrency(kpis.grossSales?.previous || 0)}`,
      deltaPct: kpis.grossSales?.deltaPct || 0,
      accentColor: '#2563eb',
      iconBg: '#dbeafe',
      icon: <AttachMoney style={{ color: '#2563eb' }} />,
    },
    {
      key: 'receivedCash',
      title: 'Recebido no Caixa',
      tooltip: 'Total efetivamente recebido em dinheiro, PIX, cartões e baixas de parcelas no período.',
      value: formattedCurrency(kpis.receivedCash?.value || 0),
      previousLabel: `Conversão: ${(kpis.receivedCash?.conversionRate || 0).toFixed(1)}% das vendas`,
      deltaPct: kpis.receivedCash?.deltaPct || 0,
      accentColor: '#059669',
      iconBg: '#d1fae5',
      icon: <AccountBalanceWallet style={{ color: '#059669' }} />,
    },
    {
      key: 'estimatedProfit',
      title: 'Lucro Bruto Estimado',
      tooltip: 'Vendas realizadas menos o preço de custo dos produtos e insumos vendidos no período.',
      value: formattedCurrency(kpis.estimatedProfit?.value || 0),
      previousLabel: `Margem bruta: ${(kpis.estimatedProfit?.marginPct || 0).toFixed(1)}%`,
      deltaPct: kpis.estimatedProfit?.deltaPct || 0,
      accentColor: '#7c3aed',
      iconBg: '#ede9fe',
      icon: <TrendingUp style={{ color: '#7c3aed' }} />,
    },
    {
      key: 'averageTicket',
      title: 'Ticket Médio',
      tooltip: 'Valor médio gasto por venda realizada no período.',
      value: formattedCurrency(kpis.averageTicket?.value || 0),
      previousLabel: `Média de ${(kpis.averageTicket?.avgItemsPerSale || 0).toFixed(1)} item(ns) por venda`,
      deltaPct: kpis.averageTicket?.deltaPct || 0,
      accentColor: '#0284c7',
      iconBg: '#e0f2fe',
      icon: <Receipt style={{ color: '#0284c7' }} />,
    },
    {
      key: 'salesCount',
      title: 'Qtd. de Vendas',
      tooltip: 'Número total de vendas concluídas ou abertas no período.',
      value: `${kpis.salesCount?.value || 0}`,
      previousLabel: `Período anterior: ${kpis.salesCount?.previous || 0} venda(s)`,
      deltaPct: kpis.salesCount?.deltaPct || 0,
      accentColor: '#ea580c',
      iconBg: '#ffedd5',
      icon: <ShoppingCart style={{ color: '#ea580c' }} />,
    },
    {
      key: 'discounts',
      title: 'Descontos Concedidos',
      tooltip: 'Total de descontos manuais e promocionais aplicados nas vendas do período.',
      value: formattedCurrency(kpis.discounts?.value || 0),
      previousLabel: `${(kpis.discounts?.discountPct || 0).toFixed(1)}% sobre o valor bruto`,
      deltaPct: kpis.discounts?.deltaPct || 0,
      invertColors: true,
      accentColor: '#64748b',
      iconBg: '#f1f5f9',
      icon: <LocalOffer style={{ color: '#64748b' }} />,
    },
  ];

  return (
    <Grid container spacing={2} style={{ marginBottom: 24 }}>
      {cards.map((card) => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
          <Paper
            elevation={0}
            style={{
              padding: 16,
              height: '100%',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              borderTop: `3px solid ${card.accentColor}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: '#fff',
            }}
          >
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1.5}
              >
                <Box
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    backgroundColor: card.iconBg,
                  }}
                >
                  {card.icon}
                </Box>
                <DeltaBadge
                  deltaPct={card.deltaPct}
                  invertColors={card.invertColors}
                />
              </Box>

              <Box display="flex" alignItems="center" mb={0.5}>
                <Typography
                  variant="caption"
                  style={{
                    color: '#64748b',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                  }}
                >
                  {card.title}
                </Typography>
                <Tooltip title={card.tooltip}>
                  <InfoOutlined
                    style={{
                      fontSize: 13,
                      color: '#94a3b8',
                      marginLeft: 4,
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              </Box>

              <Typography
                variant="h5"
                style={{
                  fontWeight: 800,
                  color: '#0f172a',
                  fontSize: '1.35rem',
                  marginBottom: 4,
                }}
              >
                {card.value}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              style={{ color: '#64748b', fontSize: '0.73rem' }}
            >
              {card.previousLabel}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export default OverviewKpiGrid;

